// App language for the website.
//
// • The member's choice is saved on their account (user/{uid}.language) so it
//   follows them to the iPhone and Android apps, and on this browser
//   (localStorage) so the page opens in the right language before sign-in
//   finishes.
// • Until someone picks a language, the browser's language is used when we
//   support it (English otherwise). Nothing is written to the account until
//   they actually choose.
// • Any text that hasn't been translated yet falls back to English, so a
//   screen is never blank.
// • Most screens use $tr("English text") / tr("English text") (see ./tr.js);
//   a few older ones use $t("key") with ./locales. Both follow the same choice.
import Vue from "vue";
import VueI18n from "vue-i18n";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db, auth } from "@/firebase";
import en from "./locales/en";
import es from "./locales/es";
import fr from "./locales/fr";
import pt from "./locales/pt";
import { tr, loadedLanguage } from "./tr";

Vue.use(VueI18n);
Vue.prototype.$tr = tr;

// Shown in their own language, so anyone can find theirs.
export const LANGUAGES = [
  { code: "en", name: "English", english: "English" },
  { code: "es", name: "Español", english: "Spanish" },
  { code: "fr", name: "Français", english: "French" },
  { code: "pt", name: "Português", english: "Portuguese" },
];

const CODES = LANGUAGES.map((l) => l.code);
const STORAGE_KEY = "tribzzLanguage";

function supported(code) {
  const c = String(code || "").toLowerCase().slice(0, 2);
  return CODES.includes(c) ? c : null;
}

function savedOnThisBrowser() {
  try { return supported(localStorage.getItem(STORAGE_KEY)); } catch (e) { return null; }
}

function browserLanguage() {
  try {
    const list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
    for (const l of list) { const c = supported(l); if (c) return c; }
  } catch (e) { /* no navigator */ }
  return "en";
}

/** The language to open in, before sign-in finishes. */
export function startingLanguage() {
  return savedOnThisBrowser() || browserLanguage();
}

export const i18n = new VueI18n({
  locale: startingLanguage(),
  fallbackLocale: "en",
  messages: { en, es, fr, pt },
  silentFallbackWarn: true,
  silentTranslationWarn: process.env.NODE_ENV === "production",
});

function apply(code, remember) {
  i18n.locale = code;
  try { document.documentElement.setAttribute("lang", code); } catch (e) { /* no document */ }
  if (remember) {
    try { localStorage.setItem(STORAGE_KEY, code); } catch (e) { /* private window */ }
  }
}
apply(i18n.locale, false);

// The page's text tables are loaded once at start-up, so a new language takes
// effect with a quick reload (the member stays on the same page).
function reloadIfNeeded(code) {
  if (code === loadedLanguage()) return;
  try { window.location.reload(); } catch (e) { /* no window */ }
}

export function languageName(code) {
  const l = LANGUAGES.find((x) => x.code === code);
  return l ? l.name : "English";
}

/** The member picked a language: switch now, remember it here and on their account. */
export async function setLanguage(code) {
  const c = supported(code);
  if (!c) return;
  apply(c, true);
  const uid = auth.currentUser && auth.currentUser.uid;
  try {
    if (uid) await setDoc(doc(db, "user", uid), { language: c }, { merge: true });
  } finally {
    reloadIfNeeded(c);
  }
}

/** After sign-in: if the account has a language (picked here or on a phone), use it. */
export async function syncLanguageWithAccount(user) {
  if (!user) return;
  try {
    const snap = await getDoc(doc(db, "user", user.uid));
    const fromAccount = snap.exists() ? supported(snap.data().language) : null;
    if (fromAccount && fromAccount !== i18n.locale) apply(fromAccount, true);
    if (fromAccount) reloadIfNeeded(fromAccount);
  } catch (e) {
    console.warn("Couldn't read the account's language:", e);
  }
}

export default i18n;